import type { GameMode } from '../core/types/gameMode.types';
import { mapGenerateExamError } from './examApi';
import { resolveInteractiveRoomUrl } from './resolveInteractiveRoomUrl';

function resolveBaseUrl(baseUrl?: string): string {
  return resolveInteractiveRoomUrl(baseUrl);
}

async function readJsonResponse<T extends Record<string, unknown>>(resp: Response): Promise<T> {
  const contentType = resp.headers.get('content-type') || '';
  const text = await resp.text();

  if (!contentType.includes('application/json')) {
    if (/^\s*</.test(text)) throw new Error('server_endpoint_unavailable');
    throw new Error('invalid_server_response');
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error('invalid_server_response');
  }
}

export async function generateActivityWithAi(params: {
  mode: GameMode;
  recipeId: string;
  recipeTitle: string;
  recipeExtension: string;
  subjectTag?: string;
  subjectExpertPrompt?: string;
  level?: string;
  difficulty?: string;
  tuning?: string;
  musicPrefs?: { readingMethod?: string; graphicMode?: string };
  courseProfile?: import('../types/courseProfile.types').CourseProfile;
  baseUrl?: string;
}): Promise<{ title: string; mode: GameMode; payload: unknown; attempts?: number }> {
  const resp = await fetch(`${resolveBaseUrl(params.baseUrl)}/api/activities/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      mode: params.mode,
      recipeId: params.recipeId,
      recipeTitle: params.recipeTitle,
      recipeExtension: params.recipeExtension,
      subjectTag: params.subjectTag,
      subjectExpertPrompt: params.subjectExpertPrompt,
      level: params.level,
      difficulty: params.difficulty,
      tuning: params.tuning,
      musicPrefs: params.musicPrefs,
      courseProfile: params.courseProfile,
    }),
  });
  const json = await readJsonResponse<{
    error?: string;
    title?: string;
    mode?: GameMode;
    payload?: unknown;
    attempts?: number;
  }>(resp);
  if (!resp.ok) throw new Error(json.error || 'generate_activity_failed');
  if (!json.payload) throw new Error('generate_activity_failed');
  return {
    title: String(json.title || params.recipeTitle),
    mode: (json.mode || params.mode) as GameMode,
    payload: json.payload,
    attempts: json.attempts,
  };
}

export function mapGenerateActivityError(code: string): string {
  return mapGenerateExamError(code);
}
